import React, { useMemo, useState } from 'react';
import { Lead, LoanStage, PipelineStage } from '../types';
import { AlertCircle, Home, Activity, SearchX, ChevronUpIcon, ChevronDownIcon, ChevronsUpDownIcon } from 'lucide-react';
interface LeadTableProps {
  leads: Lead[];
}
type SortKey = 'name' | 'stage' | 'pipelineStage' | 'daysInStage' | 'nextAction' | 'leadDate' | 'applicationDate' | 'lastContactDate' | 'loanType' | 'loanAmount' | 'subjectProperty' | 'referralSourceName';
type SortDirection = 'asc' | 'desc';
const getStageColor = (stage: LoanStage) => {
  switch (stage) {
    case 'Lead Received':
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    case 'Contact Made':
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
    case 'Application Taken':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800';
    case 'Waiting on Documentation':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
    case 'Docs Received':
      return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800';
    case 'Pre-Approved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
    case 'Accepted Offer':
      return 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800';
    case 'Need Disclosure':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  }
};
const formatCurrency = (value: number | null) => {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
const getSortValue = (lead: Lead, key: SortKey): string | number => {
  switch (key) {
    case 'name':
      return `${lead.lastName} ${lead.firstName}`.toLowerCase();
    case 'stage':
      return lead.stage;
    case 'pipelineStage':
      return lead.pipelineStage;
    case 'daysInStage':
      return lead.daysInStage;
    case 'nextAction':
      return lead.nextAction.toLowerCase();
    case 'leadDate':
      return lead.leadDate;
    case 'applicationDate':
      return lead.applicationDate || '';
    case 'lastContactDate':
      return lead.lastContactDate;
    case 'loanType':
      return `${lead.loanType} ${lead.loanProgram}`.toLowerCase();
    case 'loanAmount':
      return lead.loanAmount;
    case 'subjectProperty':
      return lead.subjectProperty.toLowerCase();
    case 'referralSourceName':
      return lead.referralSourceName.toLowerCase();
    default:
      return '';
  }
};
function SortIcon({
  sortKey,
  currentSort,
  currentDirection
}: {
  sortKey: SortKey;
  currentSort: SortKey | null;
  currentDirection: SortDirection;
}) {
  if (currentSort !== sortKey) {
    return <ChevronsUpDownIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 ml-1 inline-block" data-id="element-59" />;
  }
  return currentDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 ml-1 inline-block" data-id="element-60" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 ml-1 inline-block" data-id="element-61" />;
}
export const LeadTable: React.FC<LeadTableProps> = ({
  leads
}) => {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };
  const sortedLeads = useMemo(() => {
    if (!sortKey) return leads;
    return [...leads].sort((a, b) => {
      const aVal = getSortValue(a, sortKey);
      const bVal = getSortValue(b, sortKey);
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leads, sortKey, sortDirection]);
  const thClass = 'px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 cursor-pointer select-none hover:bg-gray-100/80 dark:hover:bg-gray-700/50 transition-colors';
  return <div className="flex-1 overflow-auto bg-white dark:bg-gray-900" data-id="element-62">
      <table className="min-w-max w-full text-left border-collapse" data-id="element-63">
        <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-20" data-id="element-64">
          <tr data-id="element-65">
            <th className="sticky left-0 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-sm px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 z-30 shadow-[1px_0_0_0_#e5e7eb] dark:shadow-[1px_0_0_0_#374151] cursor-pointer select-none hover:bg-gray-100/80 dark:hover:bg-gray-700/50 transition-colors" onClick={() => handleSort('name')} data-id="element-66">
              Borrower Name
              <SortIcon sortKey="name" currentSort={sortKey} currentDirection={sortDirection} data-id="element-67" />
            </th>
            <th className={thClass} onClick={() => handleSort('stage')} data-id="element-68">
              Current Loan Status
              <SortIcon sortKey="stage" currentSort={sortKey} currentDirection={sortDirection} data-id="element-69" />
            </th>
            <th className={thClass} onClick={() => handleSort('pipelineStage')} data-id="element-70">
              Stage
              <SortIcon sortKey="pipelineStage" currentSort={sortKey} currentDirection={sortDirection} data-id="element-71" />
            </th>
            <th className={thClass} onClick={() => handleSort('daysInStage')} data-id="element-72">
              Days In Status
              <SortIcon sortKey="daysInStage" currentSort={sortKey} currentDirection={sortDirection} data-id="element-73" />
            </th>
            <th className={thClass} onClick={() => handleSort('nextAction')} data-id="element-74">
              Next Action
              <SortIcon sortKey="nextAction" currentSort={sortKey} currentDirection={sortDirection} data-id="element-75" />
            </th>
            <th className={thClass} onClick={() => handleSort('leadDate')} data-id="element-76">
              Lead Date
              <SortIcon sortKey="leadDate" currentSort={sortKey} currentDirection={sortDirection} data-id="element-77" />
            </th>
            <th className={thClass} onClick={() => handleSort('applicationDate')} data-id="element-78">
              App Date
              <SortIcon sortKey="applicationDate" currentSort={sortKey} currentDirection={sortDirection} data-id="element-79" />
            </th>
            <th className={thClass} onClick={() => handleSort('lastContactDate')} data-id="element-80">
              Last Contact
              <SortIcon sortKey="lastContactDate" currentSort={sortKey} currentDirection={sortDirection} data-id="element-81" />
            </th>
            <th className={thClass} onClick={() => handleSort('loanType')} data-id="element-82">
              Loan Details
              <SortIcon sortKey="loanType" currentSort={sortKey} currentDirection={sortDirection} data-id="element-83" />
            </th>
            <th className={thClass} onClick={() => handleSort('loanAmount')} data-id="element-84">
              Financials
              <SortIcon sortKey="loanAmount" currentSort={sortKey} currentDirection={sortDirection} data-id="element-85" />
            </th>
            <th className={thClass} onClick={() => handleSort('subjectProperty')} data-id="element-86">
              Subject Property
              <SortIcon sortKey="subjectProperty" currentSort={sortKey} currentDirection={sortDirection} data-id="element-87" />
            </th>
            <th className={thClass} onClick={() => handleSort('referralSourceName')} data-id="element-88">
              Referral Source
              <SortIcon sortKey="referralSourceName" currentSort={sortKey} currentDirection={sortDirection} data-id="element-89" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900" data-id="element-90">
          {sortedLeads.length === 0 ? <tr data-id="element-91">
              <td colSpan={12} className="px-5 py-16 text-center" data-id="element-92">
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400" data-id="element-93">
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-full mb-3" data-id="element-94">
                    <SearchX className="w-6 h-6 text-gray-400 dark:text-gray-500" data-id="element-95" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-200" data-id="element-96">
                    No leads found
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-97">
                    Try adjusting your filters or search query.
                  </p>
                </div>
              </td>
            </tr> : sortedLeads.map(lead => <tr key={lead.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-98">
                <td className="sticky left-0 bg-white dark:bg-gray-900 group-hover:bg-gray-50/80 dark:group-hover:bg-gray-800/50 px-5 py-3.5 whitespace-nowrap z-10 shadow-[1px_0_0_0_#f3f4f6] dark:shadow-[1px_0_0_0_#1f2937] group-hover:shadow-[1px_0_0_0_#e5e7eb] dark:group-hover:shadow-[1px_0_0_0_#374151] transition-shadow" data-id="element-99">
                  <div data-id="element-100">
                    <div className="font-medium text-gray-900 dark:text-gray-100" data-id="element-101">
                      {lead.firstName} {lead.lastName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-102">
                      {lead.id}
                    </div>
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-103">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getStageColor(lead.stage)}`} data-id="element-104">
                    {lead.stage}
                  </span>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-105">
                  <span className="text-sm text-gray-900 dark:text-gray-100" data-id="element-106">
                    {lead.pipelineStage}
                  </span>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-107">
                  <div className="flex items-center" data-id="element-108">
                    <span className={`text-sm font-medium ${lead.daysInStage > 10 ? 'text-red-600 dark:text-red-400' : lead.daysInStage > 5 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`} data-id="element-109">
                      {lead.daysInStage} days
                    </span>
                    {lead.daysInStage > 10 && <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 ml-1.5" data-id="element-110" />}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-111">
                  <div className="flex items-center text-sm text-gray-900 dark:text-gray-100" data-id="element-112">
                    <Activity className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-113" />
                    {lead.nextAction}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" data-id="element-114">
                  {formatDate(lead.leadDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" data-id="element-115">
                  {formatDate(lead.applicationDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" data-id="element-116">
                  {formatDate(lead.lastContactDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-117">
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium" data-id="element-118">
                    {lead.loanType}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-119">
                    {lead.loanProgram} • {lead.loanTerm}yr
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-120">
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium" data-id="element-121">
                    {formatCurrency(lead.loanAmount)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-122">
                    {lead.purchasePrice ? `PP: ${formatCurrency(lead.purchasePrice)}` : 'Refi'}{' '}
                    • LTV: {lead.ltv}% • FICO: {lead.ficoScore}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-123">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate" data-id="element-124">
                    <Home className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0" data-id="element-125" />
                    <span className="truncate" data-id="element-126">{lead.subjectProperty}</span>
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-127">
                  <div className="text-sm text-gray-900 dark:text-gray-100" data-id="element-128">
                    {lead.referralSourceName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-129">
                    {lead.referralSourceContact}
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>
    </div>;
};